import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Space,
  Spin,
  Typography,
} from "antd";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import {
  ColorPickerField,
  FormField,
  ImageUploadBox,
  ProgressHeader,
  RepeatableSection,
  SKILL_CATEGORIES,
  SKILL_LEVELS,
  SocialLinksFields,
  StatsStep,
  WizardPageShell,
  firstArray,
  firstValue,
  useImageUpload,
} from "./WebsiteWizard";
import WebsiteGenerateModal from "./WebsiteGenerateModal";
import "./WebsiteWizard.css";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { TextArea } = Input;
const { Text } = Typography;

const STEPS = [
  {
    id: "basics",
    label: "Basics",
    icon: "👤",
    description: "Your name & personal branding",
  },
  {
    id: "hero",
    label: "Hero",
    icon: "✨",
    description: "Your website's first impression",
  },
  {
    id: "bio",
    label: "Bio",
    icon: "📖",
    description: "Who you are & your story",
  },
  {
    id: "skills",
    label: "Skills",
    icon: "⚡",
    description: "What you're great at",
  },
  {
    id: "experience",
    label: "Experience",
    icon: "💼",
    description: "Where you've worked",
  },
  {
    id: "portfolio",
    label: "Portfolio",
    icon: "🖼️",
    description: "Your best work",
  },
  {
    id: "services",
    label: "Services",
    icon: "🛠️",
    description: "What you offer",
  },
  {
    id: "stats",
    label: "Stats",
    icon: "📊",
    description: "Your numbers & achievements",
  },
  {
    id: "contact",
    label: "Contact",
    icon: "📍",
    description: "How people reach you",
  },
  {
    id: "testimonials",
    label: "Testimonials",
    icon: "💬",
    description: "What your clients say",
  },
];

// steps 2–6 are optional; contact (email) and bio (short bio) are required
const OPTIONAL_STEPS = [2, 3, 4, 5, 6, 7, 9];

function emptyFormData(userProfile) {
  return {
    fullName: [userProfile?.firstName, userProfile?.lastName]
      .filter(Boolean)
      .join(" "),
    slug: "",
    slugTouched: false,
    jobTitle: userProfile?.title ?? "",
    primaryColor: "#1a56db",
    secondaryColor: "#C9D9F2",
    avatarUri: userProfile?.profilePhoto ?? "",
    logoUri: "",

    headline: userProfile?.name ?? "",
    tagline: userProfile?.title ?? "",
    heroImageUri: "",
    ctaText: "View My Work",

    shortBio: "",
    longBio: "",
    bioImageUri: "",

    skills: [],
    experiences: [],
    projects: [],
    stats: [],
    services: [],
    testimonials: [],

    phone: userProfile?.phone ?? "",
    email: userProfile?.email ?? "",
    city: userProfile?.residentialAddress?.city ?? "",
    address: userProfile?.residentialAddress?.street ?? "",
    facebook: "",
    instagram: "",
    twitter: "",
    linkedin: "",
    youtube: "",
  };
}

function buildPortfolioOverrides(formData) {
  return {
    fullName: formData.fullName,
    slug: formData.slug,
    jobTitle: formData.jobTitle,
    primaryColor: formData.primaryColor,
    secondaryColor: formData.secondaryColor,
    // avatarUrl: formData.avatarUri,
    logoUrl: formData.logoUri,
    headline: formData.headline,
    tagline: formData.tagline,
    heroImageUrl: formData.heroImageUri,
    ctaText: formData.ctaText,
    shortBio: formData.shortBio,
    longBio: formData.longBio,
    bioImageUrl: formData.bioImageUri,
    skills: JSON.stringify(formData.skills),
    experiences: JSON.stringify(formData.experiences),
    projects: JSON.stringify(
      formData.projects.map((p) => ({
        title: p.title,
        category: p.category,
        description: p.description,
        imageUrl: p.imageUri,
        link: p.link,
      })),
    ),
    stats: JSON.stringify(formData.stats),
    services: JSON.stringify(
      formData.services.map((s) => ({
        title: s.title,
        description: s.description,
      })),
    ),
    testimonials: JSON.stringify(
      formData.testimonials.map((t) => ({
        name: t.name,
        role: t.role,
        content: t.content,
        avatarUrl: t.avatarUri,
      })),
    ),
    phone: formData.phone,
    email: formData.email,
    city: formData.city,
    address: formData.address,
    facebook: formData.facebook,
    instagram: formData.instagram,
    twitter: formData.twitter,
    linkedin: formData.linkedin,
    youtube: formData.youtube,
  };
}

function slugify(value) {
  return (value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

// steps
function Step1Basics({
  data,
  onChange,
  uploadingKey,
  progress,
  upload,
  slugStatus,
  slugMessage,
  mode,
}) {
  const handleSlug = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    onChange("slug", value);
    onChange("slugTouched", true);
  };

  const slugSuffix = {
    typing: null,
    checking: <Spin size="small" />,
    available: <span style={{ color: OLIVEEB_COLORS.oliveebGreen }}>✓</span>,
    taken: <span style={{ color: "#ef4444" }}>✕</span>,
    error: null,
    idle: null,
  }[slugStatus];

  const slugHelpColor =
    slugStatus === "taken"
      ? "#ef4444"
      : slugStatus === "available"
        ? OLIVEEB_COLORS.oliveebGreen
        : OLIVEEB_COLORS.textMuted;

  return (
    <div className="wiz-step">
      <FormField label="Full Name" required>
        <Input
          value={data.fullName}
          onChange={(e) => onChange("fullName", e.target.value)}
          placeholder="e.g. Alex Johnson"
        />
      </FormField>

      {mode != "regenerate" && (
        <Form.Item
          label={
            <Text
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: OLIVEEB_COLORS.oliveebGreen,
              }}
            >
              URL slug
            </Text>
          }
          style={{ marginBottom: 0 }}
          validateStatus={slugStatus === "taken" ? "error" : undefined}
        >
          <Space.Compact style={{ width: "100%" }}>
            <Input
              style={{
                width: 100,
                flex: "0 0 auto",
                textAlign: "center",
                borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
                fontFamily: "monospace",
                fontSize: 12,
                color: OLIVEEB_COLORS.textMuted,
                backgroundColor: "#fafafa",
              }}
              value="ovizia.com/"
              disabled
            />
            <Input
              style={{
                borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
                height: 40,
                fontSize: 13,
                fontFamily: "monospace",
              }}
              suffix={slugSuffix}
              placeholder="my-site"
              value={data.slug}
              onChange={handleSlug}
            />
          </Space.Compact>
          <Text
            style={{
              fontSize: 11,
              color: slugHelpColor,
              display: "block",
              marginBottom: 14,
            }}
          >
            {slugMessage || "Only lowercase letters, numbers, and hyphens."}
          </Text>
        </Form.Item>
      )}

      <FormField
        label="Job Title / Role"
        required
        hint="What do you do? Shown below your name"
      >
        <Input
          value={data.jobTitle}
          onChange={(e) => onChange("jobTitle", e.target.value)}
          placeholder="e.g. UI/UX Designer & Front-end Developer"
          maxLength={80}
        />
      </FormField>

      <ColorPickerField
        primaryColor={data.primaryColor}
        secondaryColor={data.secondaryColor}
        onChangePrimary={(primary, secondary) => {
          onChange("primaryColor", primary);
          onChange("secondaryColor", secondary);
        }}
      />

      <ImageUploadBox
        fieldKey="logo"
        slotType="LOGO"
        value={data.logoUri}
        onUpload={(file) =>
          upload("logo", file, "LOGO", (url) => onChange("logoUri", url))
        }
        onRemove={() => onChange("logoUri", "")}
        uploadingKey={uploadingKey}
        progress={progress}
        label="Logo"
        hint="Optional — square image works best"
        aspectLabel="PNG, JPG · Square recommended"
        height={120}
      />
    </div>
  );
}

function Step2Hero({ data, onChange, uploadingKey, progress, upload }) {
  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        This is the first thing visitors see. A strong headline makes the
        biggest impact.
      </div>

      <FormField
        label="Headline"
        required
        hint="Bold statement about what you do or offer"
      >
        <Input
          value={data.headline}
          onChange={(e) => onChange("headline", e.target.value)}
          placeholder="e.g. I Design Products People Love"
          maxLength={80}
        />
      </FormField>

      <FormField label="Tagline" hint="Short phrase shown above the headline">
        <Input
          value={data.tagline}
          onChange={(e) => onChange("tagline", e.target.value)}
          placeholder="e.g. UI Designer & Creative Developer"
          maxLength={60}
        />
      </FormField>

      <FormField label="CTA Button Text">
        <Input
          value={data.ctaText}
          onChange={(e) => onChange("ctaText", e.target.value)}
          placeholder="e.g. View My Work"
          maxLength={40}
        />
      </FormField>

      <ImageUploadBox
        fieldKey="heroImage"
        slotType="BANNER"
        value={data.heroImageUri}
        onUpload={(file) =>
          upload("heroImage", file, "BANNER", (url) =>
            onChange("heroImageUri", url),
          )
        }
        onRemove={() => onChange("heroImageUri", "")}
        uploadingKey={uploadingKey}
        progress={progress}
        label="Hero / Cover Image"
        hint="Optional background or feature image"
        aspectLabel="Landscape · 16:9 recommended"
      />
    </div>
  );
}

function Step3Bio({ data, onChange, uploadingKey, progress, upload }) {
  return (
    <div className="wiz-step">
      <FormField
        label="Short Bio"
        required
        hint="2–3 sentences — your elevator pitch"
      >
        <TextArea
          rows={3}
          value={data.shortBio}
          onChange={(e) => onChange("shortBio", e.target.value)}
          maxLength={300}
        />
      </FormField>
      <FormField
        label="Full Bio"
        hint="Your background, philosophy, what drives you"
      >
        <TextArea
          rows={4}
          value={data.longBio}
          onChange={(e) => onChange("longBio", e.target.value)}
        />
      </FormField>
      <ImageUploadBox
        fieldKey="bioImage"
        slotType="GALLERY"
        value={data.bioImageUri}
        onUpload={(file) =>
          upload("bioImage", file, "GALLERY", (url) =>
            onChange("bioImageUri", url),
          )
        }
        onRemove={() => onChange("bioImageUri", "")}
        uploadingKey={uploadingKey}
        progress={progress}
        label="Bio Photo"
        hint="A photo of you, or your team in action"
        aspectLabel="Landscape · 4:3 recommended"
      />
    </div>
  );
}

function Step4Skills({ data, onChange }) {
  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        List your skills grouped by category. Level is optional but helps
        visitors understand your expertise.
      </div>
      <RepeatableSection
        items={data.skills}
        onChange={(items) => onChange("skills", items)}
        emptyItem={{ name: "", category: "", level: "" }}
        itemLabel="Skill"
        addLabel={{ first: "Add your first skill", more: "Add another skill" }}
        emptyLabel="No skills added. Add the tools, technologies and abilities that best represent your work."
        renderItem={(item, update) => (
          <>
            <FormField label="Skill Name" required>
              <Input
                value={item.name}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="e.g. Figma, React, Photography"
              />
            </FormField>
            <FormField label="Category" hint="Group similar skills together">
              <div className="wiz-chip-grid">
                {SKILL_CATEGORIES.map((cat) => (
                  <button
                    type="button"
                    key={cat}
                    className={`wiz-chip${item.category === cat ? " is-active" : ""}`}
                    onClick={() => update({ category: cat })}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </FormField>
            <FormField label="Proficiency Level" hint="Optional">
              <div className="wiz-chip-grid">
                {SKILL_LEVELS.map((level) => (
                  <button
                    type="button"
                    key={level}
                    className={`wiz-level-chip${item.level === level ? " is-active" : ""}`}
                    onClick={() =>
                      update({ level: item.level === level ? "" : level })
                    }
                  >
                    {level}
                  </button>
                ))}
              </div>
            </FormField>
          </>
        )}
      />
    </div>
  );
}

function Step5Experience({ data, onChange }) {
  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        List your work history starting with the most recent. This builds
        credibility with potential clients.
      </div>
      <RepeatableSection
        items={data.experiences}
        onChange={(items) => onChange("experiences", items)}
        emptyItem={{ role: "", company: "", duration: "", description: "" }}
        itemLabel="Role"
        addLabel={{ first: "Add your first role", more: "Add another role" }}
        emptyLabel="No experience added. Even freelance or personal projects count."
        renderItem={(item, update) => (
          <>
            <div className="wiz-two-col">
              <FormField label="Job Title" required>
                <Input
                  value={item.role}
                  onChange={(e) => update({ role: e.target.value })}
                  placeholder="e.g. Senior Designer"
                />
              </FormField>
              <FormField label="Company" required>
                <Input
                  value={item.company}
                  onChange={(e) => update({ company: e.target.value })}
                  placeholder="e.g. Acme Corp"
                />
              </FormField>
            </div>
            <FormField label="Duration" hint="e.g. Jan 2020 – Present">
              <Input
                value={item.duration}
                onChange={(e) => update({ duration: e.target.value })}
                placeholder="e.g. 2020 – Present"
              />
            </FormField>
            <FormField
              label="Description"
              hint="2–3 sentences on what you accomplished"
            >
              <TextArea
                rows={2}
                value={item.description}
                onChange={(e) => update({ description: e.target.value })}
              />
            </FormField>
          </>
        )}
      />
    </div>
  );
}

function Step6Portfolio({ data, onChange, uploadingKey, progress, upload }) {
  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        Show your best work. 3–6 projects is the sweet spot — quality over
        quantity.
      </div>
      <RepeatableSection
        items={data.projects}
        onChange={(items) => onChange("projects", items)}
        emptyItem={{
          title: "",
          category: "",
          description: "",
          imageUri: "",
          link: "",
        }}
        itemLabel="Project"
        addLabel={{
          first: "Add your first project",
          more: "Add another project",
        }}
        emptyLabel="No projects added. Add your best work — even personal or side projects."
        renderItem={(item, update, index) => (
          <>
            <FormField label="Project Title" required>
              <Input
                value={item.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="e.g. Fintech Mobile App Redesign"
              />
            </FormField>
            <FormField label="Project Category" required>
              <Input
                value={item.category}
                onChange={(e) => update({ category: e.target.value })}
                placeholder="e.g. Mobile App"
              />
            </FormField>
            <FormField
              label="Description"
              hint="What was the project? What did you do? What was the outcome?"
            >
              <TextArea
                rows={2}
                value={item.description}
                onChange={(e) => update({ description: e.target.value })}
              />
            </FormField>
            <FormField
              label="Project Link"
              hint="Optional — live project, case study or GitHub"
            >
              <Input
                value={item.link}
                onChange={(e) => update({ link: e.target.value })}
                placeholder="https://"
              />
            </FormField>
            <ImageUploadBox
              fieldKey={`project_${index}`}
              slotType="GALLERY"
              value={item.imageUri}
              onUpload={(file) =>
                upload(`project_${index}`, file, "GALLERY", (url) =>
                  update({ imageUri: url }),
                )
              }
              onRemove={() => update({ imageUri: "" })}
              uploadingKey={uploadingKey}
              progress={progress}
              label="Project Image"
              hint="Screenshot, mockup or photo of the project"
              aspectLabel="16:9 recommended"
              height={140}
            />
          </>
        )}
      />
    </div>
  );
}

function Step7Services({ data, onChange }) {
  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        List the services you offer as a freelancer or professional.
      </div>
      <RepeatableSection
        items={data.services}
        onChange={(items) => onChange("services", items)}
        emptyItem={{ title: "", description: "" }}
        itemLabel="Service"
        addLabel={{
          first: "Add your first service",
          more: "Add another service",
        }}
        emptyLabel="No services added. Skip this if you're not offering freelance work."
        renderItem={(item, update) => (
          <>
            <FormField label="Service Name" required>
              <Input
                value={item.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="e.g. Brand Identity Design"
              />
            </FormField>
            <FormField
              label="Description"
              hint="What's included? Who is it for?"
            >
              <TextArea
                rows={2}
                value={item.description}
                onChange={(e) => update({ description: e.target.value })}
              />
            </FormField>
          </>
        )}
      />
    </div>
  );
}

function Step9Contact({ data, onChange }) {
  return (
    <div className="wiz-step">
      <Card size="small" className="wiz-card" title="Contact Details">
        <FormField label="Email Address" required>
          <Input
            value={data.email}
            onChange={(e) => onChange("email", e.target.value)}
            placeholder="e.g. alex@example.com"
          />
        </FormField>
        <FormField
          label="Phone Number"
          hint="Optional — only add if you want clients to call you"
        >
          <Input
            value={data.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            placeholder="e.g. +1 555 123 4567"
          />
        </FormField>
        <FormField label="City">
          <Input
            value={data.city}
            onChange={(e) => onChange("city", e.target.value)}
            placeholder="e.g. San Francisco, CA"
          />
        </FormField>
        <FormField label="Address">
          <Input
            value={data.address}
            onChange={(e) => onChange("address", e.target.value)}
            placeholder="e.g. 123 Main Street"
          />
        </FormField>
      </Card>

      <Card
        size="small"
        className="wiz-card"
        title="Social & Professional Links"
      >
        <SocialLinksFields data={data} onChange={onChange} />
      </Card>
    </div>
  );
}

function Step10Testimonials({
  data,
  onChange,
  uploadingKey,
  progress,
  upload,
}) {
  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        Real quotes from happy clients build trust instantly. Even one strong
        testimonial makes a difference.
      </div>
      <RepeatableSection
        items={data.testimonials}
        onChange={(items) => onChange("testimonials", items)}
        emptyItem={{ name: "", role: "", content: "", avatarUri: "" }}
        itemLabel="Testimonial"
        addLabel={{
          first: "Add your first testimonial",
          more: "Add another testimonial",
        }}
        emptyLabel="No testimonials added. The AI will generate placeholder testimonials."
        renderItem={(item, update, index) => (
          <>
            <FormField label="Quote" required>
              <TextArea
                rows={2}
                value={item.content}
                onChange={(e) => update({ content: e.target.value })}
              />
            </FormField>
            <div className="wiz-two-col">
              <FormField label="Name" required>
                <Input
                  value={item.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="e.g. Sarah J."
                />
              </FormField>
              <FormField label="Role / Title">
                <Input
                  value={item.role}
                  onChange={(e) => update({ role: e.target.value })}
                  placeholder="e.g. Client"
                />
              </FormField>
            </div>
            <ImageUploadBox
              fieldKey={`avatar_${index}`}
              slotType="PROFILE_PHOTO"
              value={item.avatarUri}
              onUpload={(file) =>
                upload(`avatar_${index}`, file, "PROFILE_PHOTO", (url) =>
                  update({ avatarUri: url }),
                )
              }
              onRemove={() => update({ avatarUri: "" })}
              uploadingKey={uploadingKey}
              progress={progress}
              label="Client Photo"
              hint="Optional — square photo"
              aspectLabel="Square · 1:1"
              height={100}
              circle
            />
          </>
        )}
      />
    </div>
  );
}

// main
export default function WebsiteBuildPortfolio() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const websiteId = searchParams.get("websiteId");
  const mode = searchParams.get("mode");

  const [template, setTemplate] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [loadingPrefill, setLoadingPrefill] = useState(mode === "regenerate");

  const [formData, setFormData] = useState(() => emptyFormData(user));

  const [slugStatus, setSlugStatus] = useState("idle"); // idle | checking | available | taken | error
  const [slugMessage, setSlugMessage] = useState("");
  const slugCheckSeq = useRef(0);
  const slugDebounceRef = useRef(null);

  const { uploadingKey, upload } = useImageUpload((file, slotType) =>
    olreadiaService.websites.uploadImageForWorkspace(
      selectedWorkspaceId,
      file,
      slotType,
      actorId,
    ),
  );

  useEffect(() => {
    if (!templateId) return;
    olreadiaService.websites
      .getTemplate(templateId, actorId)
      .then(setTemplate)
      .catch(() => {});
  }, [templateId, actorId]);

  // prefill from existing content when regenerating
  useEffect(() => {
    if (mode !== "regenerate" || !websiteId) return;
    let cancelled = false;

    (async () => {
      try {
        const activeContent = await olreadiaService.websites.activeContent(
          websiteId,
          actorId,
        );
        let content = { sections: [] };
        try {
          content = JSON.parse(activeContent.contentJson);
        } catch {
          // ignore malformed content
        }
        if (cancelled) return;

        const section = (type) =>
          content.sections?.find((s) => s.type === type)?.data;
        const hero = section("HERO");
        const bio = section("BIO");
        const skillsData = section("SKILLS");
        const expData = section("EXPERIENCE");
        const portfolioData = section("PORTFOLIO_ITEMS");
        const servicesData = section("SERVICES");
        const testimonialsData = section("TESTIMONIALS");
        const contact = section("CONTACT");
        const statsData = section("STATS");

        setFormData((prev) => ({
          ...prev,
          fullName: bio?.fullName ?? prev.fullName,
          slug: content.slug ?? prev.slug,
          slugTouched: content.slug ? true : prev.slugTouched,
          jobTitle: bio?.jobTitle ?? prev.jobTitle,
          logoUri: content.logo?.imageUrl ?? prev.logoUri,
          primaryColor:
            content.themeSettings?.primaryColor ?? prev.primaryColor,
          secondaryColor:
            content.themeSettings?.secondaryColor ?? prev.secondaryColor,

          headline: hero?.headline ?? prev.headline,
          tagline: hero?.tagline ?? prev.tagline,
          heroImageUri: hero?.imageUrl ?? prev.heroImageUri,
          ctaText: hero?.ctaText ?? prev.ctaText,

          shortBio: bio?.shortBio ?? prev.shortBio,
          longBio: bio?.longBio ?? prev.longBio,
          bioImageUri: bio?.imageUrl ?? prev.bioImageUri,

          skills: firstArray(
            (skillsData?.items ?? []).map((s) => ({
              name: s.name ?? "",
              category: s.category ?? "",
              level: s.level ?? "",
            })),
            prev.skills,
          ),
          experiences: firstArray(
            (expData?.items ?? []).map((e) => ({
              role: e.role ?? "",
              company: e.company ?? "",
              duration: e.duration ?? "",
              description: e.description ?? "",
            })),
            prev.experiences,
          ),
          projects: firstArray(
            (portfolioData?.items ?? []).map((p) => ({
              title: p.title ?? "",
              category: p.category ?? "",
              description: p.description ?? "",
              imageUri: p.imageUrl ?? "",
              link: p.link ?? "",
            })),
            prev.projects,
          ),
          services: firstArray(
            (servicesData?.items ?? []).map((s) => ({
              title: s.title ?? "",
              description: s.shortDescription ?? s.description ?? "",
            })),
            prev.services,
          ),
          testimonials: firstArray(
            (testimonialsData?.items ?? []).map((t) => ({
              name: t.name ?? "",
              role: t.role ?? "",
              content: t.content ?? "",
              avatarUri: t.avatarUrl ?? "",
            })),
            prev.testimonials,
          ),
          stats: firstArray(
            (statsData?.items ?? []).map((s) => ({
              type: s.label ?? "",
              value: s.value ?? "",
              suffix: s.suffix ?? "+",
              label: s.label ?? "",
            })),
            prev.stats,
          ),

          phone: contact?.primaryPhone ?? prev.phone,
          email: contact?.primaryEmail ?? prev.email,
          city: contact?.city ?? prev.city,
          address: contact?.address ?? prev.address,
          facebook: contact?.socials?.facebook ?? prev.facebook,
          instagram: contact?.socials?.instagram ?? prev.instagram,
          twitter: contact?.socials?.twitter ?? prev.twitter,
          linkedin: contact?.socials?.linkedin ?? prev.linkedin,
          youtube: contact?.socials?.youtube ?? prev.youtube,
        }));
      } catch {
        // fall back to profile prefill silently
      } finally {
        if (!cancelled) setLoadingPrefill(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, websiteId, actorId]);

  // keep slug in sync with full name until the user edits it directly
  useEffect(() => {
    if (mode === "regenerate") return;
    if (formData.slugTouched) return;
    const derived = slugify(formData.fullName);
    if (derived !== formData.slug) {
      setFormData((prev) => ({ ...prev, slug: derived }));
    }
  }, [formData.fullName, formData.slugTouched]);

  const runSlugCheck = useCallback(
    async (slug) => {
      setSlugStatus("checking");
      const seq = ++slugCheckSeq.current;
      try {
        const res = await olreadiaService.websites.checkSlug(slug, actorId);
        if (seq !== slugCheckSeq.current) return;
        setSlugStatus(res.available ? "available" : "taken");
        setSlugMessage(res.message);
      } catch {
        if (seq !== slugCheckSeq.current) return;
        setSlugStatus("error");
        setSlugMessage("Could not check slug availability right now.");
      }
    },
    [actorId],
  );

  // debounced check whenever slug changes
  useEffect(() => {
    if (mode === "regenerate") return;
    const slug = formData.slug;

    if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);

    if (!slug || slug.length < 3) {
      setSlugStatus("idle");
      setSlugMessage("");
      slugCheckSeq.current++; // invalidate any in-flight check
      return;
    }

    // show "typing" (not "checking") immediately — no spinner-looks-active confusion
    setSlugStatus("typing");
    slugCheckSeq.current++; // invalidate any in-flight check from a prior value

    slugDebounceRef.current = setTimeout(() => {
      runSlugCheck(slug);
    }, 700); // bumped from 400ms

    return () => {
      if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
    };
  }, [formData.slug, runSlugCheck]);

  const onChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validateStep = () => {
    const newErrors = {};
    if (currentStep === 0) {
      if (!formData.fullName.trim())
        newErrors.fullName = "Full name is required";
      if (!formData.slug.trim()) newErrors.slug = "URL slug is required";
      else if (slugStatus === "taken")
        newErrors.slug = "This slug is already taken";
      else if (slugStatus === "checking")
        newErrors.slug = "Still checking slug availability…";
      if (!formData.jobTitle.trim())
        newErrors.jobTitle = "Job title is required";
    }
    if (currentStep === 1 && !formData.headline.trim())
      newErrors.headline = "Headline is required";
    if (currentStep === 2 && !formData.shortBio.trim())
      newErrors.shortBio = "Short bio is required";
    if (currentStep === 8 && !formData.email.trim())
      newErrors.email = "Email address is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      handleGenerate();
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      navigate(-1);
    } else {
      setCurrentStep((s) => s - 1);
    }
  };

  const runGenerate = async () => {
    const overrides = buildPortfolioOverrides(formData);
    return mode === "regenerate" && websiteId
      ? olreadiaService.websites.regenerate(
          websiteId,
          templateId,
          overrides,
          actorId,
        )
      : olreadiaService.websites.bootstrap(
          {
            workspaceId: selectedWorkspaceId,
            name: formData.fullName,
            slug: formData.slug,
            description: formData.description,
            template: templateId,
            websiteType: "PORTFOLIO",
            overrides,
          },
          actorId,
        );
  };

  const handleGenerate = () => {
    if (!actorId) return;
    setGenerateModalOpen(true);
  };

  const handleEditWebsite = (result) => {
    const nextWebsiteId = result.websiteId || websiteId || "";
    window.open(
      `https://dev.ovizia.com/editor/${nextWebsiteId}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleClose = () => {
    setGenerateModalOpen(false);
    navigate(`/website?wid=${selectedWorkspaceId}`);
  };

  const stepProps = {
    data: formData,
    onChange,
    uploadingKey,
    // progress,
    upload,
    mode,
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <Step1Basics {...stepProps} />;
      case 1:
        return <Step2Hero {...stepProps} />;
      case 2:
        return <Step3Bio {...stepProps} />;
      case 3:
        return <Step4Skills data={formData} onChange={onChange} />;
      case 4:
        return <Step5Experience data={formData} onChange={onChange} />;
      case 5:
        return <Step6Portfolio {...stepProps} />;
      case 6:
        return <Step7Services data={formData} onChange={onChange} />;
      case 7:
        return (
          <StatsStep
            stats={formData.stats}
            setStats={(v) => onChange("stats", v)}
          />
        );
      case 8:
        return <Step9Contact data={formData} onChange={onChange} />;
      case 9:
        return <Step10Testimonials {...stepProps} />;
      default:
        return null;
    }
  };

  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <>
      <WizardPageShell
        eyebrow="Build personal website"
        title={
          mode === "regenerate"
            ? "Update Website Info"
            : "Create Personal Website"
        }
        description={`${template?.name || templateId} • Add your professional summary, services, contact details, and career story.`}
        onGenerate={handleGenerate}
        generating={false}
      >
        {loadingPrefill ? (
          <div
            style={{ display: "flex", justifyContent: "center", padding: 80 }}
          >
            <Spin size="large" description="Loading your portfolio content…" />
          </div>
        ) : (
          <div className="wiz-layout">
            <ProgressHeader
              steps={STEPS}
              currentStep={currentStep}
              onBack={handleBack}
            />

            <div className="wiz-body">
              <div className="wiz-step-header">
                <Text type="secondary">{STEPS[currentStep].description}</Text>
                {OPTIONAL_STEPS.includes(currentStep) ? (
                  <span className="wiz-optional-badge">
                    Optional — skip if you prefer
                  </span>
                ) : null}
              </div>

              <Card className="wiz-card-outer">{renderStep()}</Card>

              {Object.values(errors)
                .filter(Boolean)
                .map((err, i) => (
                  <Alert
                    key={i}
                    type="error"
                    showIcon
                    title={err}
                    className="wiz-error"
                  />
                ))}
            </div>

            <div className="wiz-footer">
              <span className="wiz-template-badge">
                🎨 {template?.name || templateId}
              </span>
              <div className="wiz-footer-actions">
                {currentStep > 0 ? (
                  <Button onClick={handleBack}>← Back</Button>
                ) : null}
                <Button type="primary" onClick={handleNext}>
                  {isLastStep ? "⚡ Generate Portfolio" : "Next →"}
                </Button>
              </div>
              {!isLastStep ? (
                <button
                  type="button"
                  className="wiz-skip-link"
                  onClick={handleGenerate}
                >
                  Skip remaining steps and generate now
                </button>
              ) : null}
            </div>
          </div>
        )}
      </WizardPageShell>

      <WebsiteGenerateModal
        open={generateModalOpen}
        onClose={handleClose}
        template={template}
        run={runGenerate}
        onEdit={handleEditWebsite}
        getPreviewUrl={olreadiaService.websites.previewUrl}
      />
    </>
  );
}
